import { create } from 'zustand';
import { FundingSource } from '../types';
import { fundingSourcesApi } from '../api/fundingSources';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('store:funding');

// Keyed on the sorted area ids a load was made for, so a repeat call with
// the same areas (e.g. a remount) reuses the cached result instead of
// re-fetching. Module-level, same reasoning as mapOverlaysStore's
// overlaysKey: a zustand store's own state isn't a safe place to compare
// against inside a single set() call.
let loadedKey: string | null = null;

interface FundingState {
  sources: FundingSource[];
  loading: boolean;
  error: string | null;

  // Loads once per distinct areaIds set; safe to call on every mount.
  load: (areaIds: string[]) => Promise<void>;
  reset: () => void;
}

export const useFundingStore = create<FundingState>((set, get) => ({
  sources: [],
  loading: false,
  error: null,

  load: async (areaIds) => {
    const key = [...areaIds].sort().join(',');
    if (loadedKey === key) return;
    if (get().loading) return;

    set({ loading: true, error: null });
    try {
      const sources = await fundingSourcesApi.listFundingSources(areaIds);
      loadedKey = key;
      set({ sources, loading: false });
    } catch (error) {
      log.warn('load failed:', error);
      set({ loading: false, error: (error as Error).message });
    }
  },

  reset: () => {
    loadedKey = null;
    set({ sources: [], loading: false, error: null });
  },
}));
